import { useDeviceIdContext } from '@/components/device-id-provider';
import { Button } from '@/components/ui/button';
import { useHeyGenToken } from '@/hooks/useHeyGenToken';
import { cn } from '@/lib/utils';
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskType,
} from '@heygen/streaming-avatar';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Mic,
  MicOff,
  PhoneOff,
  Volume2,
  VolumeX,
  Check,
  Loader2,
} from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAvatarConfig, type AvatarCompanyConfig } from './avatar-configs';
import { UOBVisuals } from './UOBVisuals';

// =============================================================================
// WRAPPER COMPONENT (for router)
// =============================================================================

export function ScriptedAvatarCallWrapper() {
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('company') || 'uob';

  let config;
  try {
    config = getAvatarConfig(companyCode);
  } catch (error) {
    window.location.href = '/avatar?company=uob';
    return null;
  }

  return <ScriptedAvatarCall companyConfig={config} mode="video" />;
}

// =============================================================================
// SCRIPT DEFINITION - UOB Financial Consultant Training
// =============================================================================

interface ScriptLine {
  speaker: 'ai' | 'user';
  text: string;
  stage?: number;
  visualTrigger?: string;
}

const UOB_SCRIPT: ScriptLine[] = [
  // Stage 0: Introduction
  {
    speaker: 'ai',
    text: "Welcome, I'm here to guide you through the essential rules and ethics that form the foundation of our profession as a financial consultant in Singapore. Today we'll be focusing on the Financial Advisers Act, F-A-A, and the Financial Advisers Regulations, F-A-R. Feel free to ask questions in between for any clarification. We will have a quiz at the end of the session. Ready to begin?",
    stage: 0,
  },
  { speaker: 'user', text: 'Yes.' },

  // Stage 1: Core Principles
  {
    speaker: 'ai',
    text: "Let's begin with the four core principles that underpin the Financial Advisers Act and its Regulations. These are the ethical backbone of your role as a financial consultant. A. – Customers' Interest: Always act in the client's best interest. B. – Consistency: Similar products and services must follow the same rule. C. – Accountability: Each representative can only act for one principal. D. – Independence: no commissions, no product bias, and full objectivity. These principles guide how you interact with clients, recommend products, and represent your firm.",
    stage: 1,
    visualTrigger: 'principles-all',
  },
  { speaker: 'user', text: "Can you explain a bit more about the Consistency part? I'm not sure I fully get it." },
  {
    speaker: 'ai',
    text: "Absolutely! Consistency means that the same rules apply across different channels and similar products. For example, if two clients buy the same type of unit trust—one from a bank adviser and one from an independent adviser—both advisers must follow the same regulatory standards. It ensures fairness and protects clients from receiving different treatment just because of where they bought the product.",
    stage: 1,
    visualTrigger: 'principles-B',
  },
  { speaker: 'user', text: "Okay, I understand now. Does using the term independent be strictly regulated? It sounds like a strong word." },
  {
    speaker: 'ai',
    text: "Yes you are right. A firm can only call itself independent if it meets very high standards, such as receiving no commissions from product providers that could create bias.",
    stage: 1,
    visualTrigger: 'principles-D',
  },
  { speaker: 'user', text: "Wait, Why is it a principle if it's more like a marketing rule?" },
  {
    speaker: 'ai',
    text: "That's a sharp observation. It is tightly linked to marketing, but its inclusion as a principle highlights a deeper philosophical point. The regulators considered that true structural independence is very difficult to achieve and verify. More importantly, they determined that putting the customer's interest first does not require structural independence. What matters most is that advice is objective and based on a reasonable basis, regardless of how the firm is paid. So, the Independence principle is less about encouraging that model and more about strictly defining and controlling it to prevent misleading consumers. It ensures that if a firm claims to be independent, it truly is free from the conflicts of interest that the term implies. It protects the integrity of that label.",
    stage: 1,
    visualTrigger: 'principles-D',
  },
  { speaker: 'user', text: "Ah, that makes sense. It's about protecting the meaning of the word to prevent mis-selling. Ok, I want to know if there are entities that don't need a financial adviser's licence to provide services?" },

  // Stage 2: Exempt & Excluded Persons
  {
    speaker: 'ai',
    text: "That's Exempt Persons under the F-A-A. Examples include: Licensed banks and insurance companies, Capital markets services licence holders, Certain co-operative societies and finance companies, Approved exchanges and market operators. These entities are already regulated under other Acts, so they're exempt from holding a separate F-A-A licence.",
    stage: 2,
    visualTrigger: 'exempt-only',
  },
  { speaker: 'user', text: "Wait—what's the difference between an exempt person and an excluded person? And why does it matter?" },
  {
    speaker: 'ai',
    text: "Great question! Let's break it down. Exempt Persons are organisations that are still actively providing financial advisory services, but they're already regulated under other financial laws. Think of them as covered elsewhere. For example, a bank is regulated under the Banking Act, so M-A-S doesn't require it to get a separate F-A-A licence. However, they still need to follow F-A-A conduct rules, and their representatives must be fit and proper. Excluded Persons, on the other hand, are people or entities whose financial advice is only incidental to their main job. For instance, a lawyer giving general financial guidance as part of estate planning, or a newspaper publishing investment articles without earning commissions. They're not considered financial advisers under the F-A-A at all. Why does this matter? Because it affects what kind of oversight and obligations apply. Exempt persons are still under M-A-S's watch for conduct, while excluded persons are not subject to the F-A-A's licensing or conduct rules—unless they cross the line into full advisory services.",
    stage: 2,
    visualTrigger: 'comparison',
  },
  { speaker: 'user', text: "Ah, so exempt means licensed elsewhere but still active, and excluded means not really in the business of advising. Got it." },
  {
    speaker: 'ai',
    text: "Exactly! You nailed it.",
    stage: 2,
  },

  // Stage 3: Quiz
  {
    speaker: 'ai',
    text: "Alright! Pop-up quiz. Which principle ensures that a financial adviser cannot represent more than one principal at the same time?",
    stage: 3,
    visualTrigger: 'quiz-1',
  },
  { speaker: 'user', text: "Accountability.", visualTrigger: 'quiz-1-user-answer-C' },
  {
    speaker: 'ai',
    text: "Correct! That's the principle of Accountability. It ensures that clients know exactly who is responsible for the advice they receive.",
    stage: 3,
    visualTrigger: 'quiz-1-reveal',
  },
  {
    speaker: 'ai',
    text: "Question 2: Which of the following is considered an exempt person under the F-A-A? A. A freelance financial blogger. B. A licensed bank providing investment advice. C. A tied agent representing two insurance firms. D. A newspaper columnist who receives commission for product promotion.",
    stage: 3,
    visualTrigger: 'quiz-2',
  },
  { speaker: 'user', text: "Ummm. A newspaper columnist who receives commission for product promotion.", visualTrigger: 'quiz-2-user-answer-D' },
  {
    speaker: 'ai',
    text: "That's a thoughtful guess, but not quite right. According to the F-A-A, a newspaper columnist can only be considered an excluded person—not an exempt one—and only if they don't receive commissions or other compensation beyond subscription fees. Since this option mentions commission, it disqualifies them. The correct answer is B—a licensed bank is an exempt person under the F-A-A because it's already regulated under the Banking Act. Keep that distinction in mind: exempt persons are typically institutions regulated under other Acts, while excluded persons are those whose advisory activities are incidental to their main business.",
    stage: 3,
    visualTrigger: 'quiz-2-reveal',
  },
  { speaker: 'user', text: "Okay, thanks for the clarification." },
  {
    speaker: 'ai',
    text: "Do you have any other questions regarding the principles and exempt person rules?",
    stage: 3,
    visualTrigger: 'quiz-complete', // Clear quiz overlay
  },
  { speaker: 'user', text: "I'm good." },

  // Stage 4: Conclusion
  {
    speaker: 'ai',
    text: "Great. We will cover the Types Of Financial Advisory Services in the next module. Bye!",
    stage: 4,
  },
];

// =============================================================================
// COMPONENT - Looks exactly like UnifiedAvatarCall but uses scripted speak()
// =============================================================================

interface ScriptedAvatarCallProps {
  companyConfig: AvatarCompanyConfig;
  mode: 'voice' | 'video';
}

export function ScriptedAvatarCall({ companyConfig, mode }: ScriptedAvatarCallProps) {
  const navigate = useNavigate();
  const { deviceId } = useDeviceIdContext();
  const { token: heyGenToken, generateToken } = useHeyGenToken();

  // Script state (hidden from UI)
  const [scriptIndex, setScriptIndex] = useState(0);
  const scriptIndexRef = useRef(0);
  const isWaitingForUserRef = useRef(false);
  const hasStartedRef = useRef(false);

  // Stage names for progress tracker
  const stageNames = companyConfig.stages?.names || ['Introduction', 'Core Principles', 'Exempt & Excluded', 'Knowledge Check', 'Complete'];
  const [currentStage, setCurrentStage] = useState(0);

  // UOB visual states
  const [activePrinciple, setActivePrinciple] = useState<string | null>(null);
  const [exemptMode, setExemptMode] = useState<'exempt-only' | 'comparison' | null>(null);
  const [quizQuestion, setQuizQuestion] = useState(1);
  const [quizState, setQuizState] = useState<'waiting' | 'answered' | 'revealed'>('waiting');
  const [userQuizAnswer, setUserQuizAnswer] = useState<string | null>(null);

  // UI states (same as UnifiedAvatarCall)
  const [isInitializing, setIsInitializing] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [userStream, setUserStream] = useState<MediaStream | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const avatarRef = useRef<StreamingAvatar | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const avatarVideoElementRef = useRef<HTMLVideoElement | null>(null);
  const isLeavingRef = useRef(false);
  const hasInitializedRef = useRef(false);

  // Update visual state based on script line
  const updateVisualsForLine = useCallback((line: ScriptLine) => {
    if (line.stage !== undefined) {
      setCurrentStage(line.stage);
    }

    if (line.visualTrigger) {
      switch (line.visualTrigger) {
        // Principles
        case 'principles-all':
          setActivePrinciple('ALL');
          setExemptMode(null);
          break;
        case 'principles-B':
          setActivePrinciple('B');
          setExemptMode(null);
          break;
        case 'principles-D':
          setActivePrinciple('D');
          setExemptMode(null);
          break;

        // Exempt/Excluded
        case 'exempt-only':
          setActivePrinciple(null);
          setExemptMode('exempt-only');
          break;
        case 'comparison':
          setActivePrinciple(null);
          setExemptMode('comparison');
          break;

        // Quiz 1
        case 'quiz-1':
          setActivePrinciple(null);
          setExemptMode(null);
          setQuizQuestion(1);
          setQuizState('waiting');
          setUserQuizAnswer(null);
          break;
        case 'quiz-1-user-answer-C':
          // User answered C (Accountability) - show as selected
          setUserQuizAnswer('C');
          setQuizState('answered');
          break;
        case 'quiz-1-reveal':
          // Avatar says "Correct!" - reveal the answer
          setQuizState('revealed');
          break;

        // Quiz 2
        case 'quiz-2':
          setQuizQuestion(2);
          setQuizState('waiting');
          setUserQuizAnswer(null);
          break;
        case 'quiz-2-user-answer-D':
          // User answered D (wrong answer) - show as selected
          setUserQuizAnswer('D');
          setQuizState('answered');
          break;
        case 'quiz-2-reveal':
          // Avatar explains correct answer is B - reveal
          setQuizState('revealed');
          break;
        case 'quiz-complete':
          // Clear quiz overlay after all questions done
          setQuizQuestion(0); // Set to 0 to hide quiz
          setQuizState('waiting');
          setUserQuizAnswer(null);
          break;
      }
    } else if (line.speaker === 'ai') {
      // Clear overlays when moving to different stages without visual trigger
      if (line.stage !== 1) setActivePrinciple(null);
      if (line.stage !== 2) setExemptMode(null);
    }
  }, []);

  // Speak current AI line
  const speakLine = useCallback(async (index: number) => {
    const line = UOB_SCRIPT[index];
    if (!avatarRef.current || !line || line.speaker !== 'ai') {
      console.warn('[Script] Cannot speak - no avatar or not AI turn');
      return;
    }

    console.log(`[Script] Speaking line ${index}:`, line.text.substring(0, 50) + '...');
    updateVisualsForLine(line);

    setIsProcessing(true);
    setIsListening(false);

    try {
      // Use REPEAT to speak exact text (TALK uses AI to generate response)
      await avatarRef.current.speak({
        text: line.text,
        taskType: TaskType.REPEAT,
      });
    } catch (err) {
      console.error('[Script] Failed to speak:', err);
      setIsProcessing(false);
    }
  }, [updateVisualsForLine]);

  // Advance to next line
  const advanceScript = useCallback(() => {
    // First, check if current line has a visual trigger (for user lines, triggers fire when they finish)
    const currentIndex = scriptIndexRef.current;
    const currentLine = UOB_SCRIPT[currentIndex];
    if (currentLine && currentLine.speaker === 'user' && currentLine.visualTrigger) {
      console.log(`[Script] Triggering visual for user line: ${currentLine.visualTrigger}`);
      updateVisualsForLine(currentLine);
    }

    const nextIndex = currentIndex + 1;
    if (nextIndex >= UOB_SCRIPT.length) {
      console.log('[Script] Script complete!');
      setCurrentStage(4);
      setIsProcessing(false);
      setIsListening(false);
      // Clear all overlays
      setActivePrinciple(null);
      setExemptMode(null);
      return;
    }

    console.log(`[Script] Advancing to line ${nextIndex}`);
    scriptIndexRef.current = nextIndex;
    setScriptIndex(nextIndex);

    const nextLine = UOB_SCRIPT[nextIndex];
    if (nextLine.speaker === 'ai') {
      isWaitingForUserRef.current = false;
      speakLine(nextIndex);
    } else {
      // User turn - show listening state, wait for spacebar
      isWaitingForUserRef.current = true;
      setIsProcessing(false);
      setIsListening(true);
      console.log('[Script] Waiting for user (press SPACE when done)...');
    }
  }, [speakLine, updateVisualsForLine]);

  // Handle user advancing (spacebar press)
  const handleUserAdvance = useCallback(() => {
    if (!isWaitingForUserRef.current) return;

    console.log('[Script] User acknowledged, advancing...');
    isWaitingForUserRef.current = false;
    advanceScript();
  }, [advanceScript]);

  // Keyboard listener for spacebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && isWaitingForUserRef.current) {
        e.preventDefault();
        handleUserAdvance();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUserAdvance]);

  // Auto-start on mount
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      handleStartCall();
    }

    return () => {
      if (userStream) {
        userStream.getTracks().forEach((track) => track.stop());
      }
      if (avatarRef.current && !isLeavingRef.current) {
        avatarRef.current.stopAvatar().catch(console.error);
      }
    };
  }, []);

  // Initialize user media
  const initializeUserMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
      setUserStream(stream);
      return stream;
    } catch (error) {
      console.error('Failed to access microphone:', error);
      setError('Unable to access microphone. Please grant permissions.');
      throw error;
    }
  };

  // Initialize HeyGen avatar
  const initializeAvatar = async () => {
    try {
      let token = heyGenToken;
      if (!token) {
        token = await generateToken();
      }

      if (!token) {
        throw new Error('Failed to get HeyGen token');
      }

      const avatar = new StreamingAvatar({ token });
      avatarRef.current = avatar;

      // Event listeners
      avatar.on(StreamingEvents.AVATAR_START_TALKING, () => {
        setIsProcessing(true);
        setIsListening(false);
      });

      avatar.on(StreamingEvents.AVATAR_STOP_TALKING, () => {
        setIsProcessing(false);
        // Advance after avatar finishes speaking
        setTimeout(() => {
          advanceScript();
        }, 500);
      });

      avatar.on(StreamingEvents.STREAM_READY, (event: any) => {
        console.log('[Script] STREAM_READY');
        setIsInitializing(false);

        setTimeout(() => {
          if (videoContainerRef.current && event.detail) {
            let videoElement: HTMLVideoElement | null = null;

            if (event.detail.htmlElement) {
              videoElement = event.detail.htmlElement;
            } else if (event.detail instanceof MediaStream) {
              videoElement = document.createElement('video');
              videoElement.srcObject = event.detail;
              videoElement.autoplay = true;
              videoElement.playsInline = true;
              videoElement.style.width = '100%';
              videoElement.style.height = '100%';
              videoElement.style.objectFit = 'cover';
            }

            if (videoElement) {
              avatarVideoElementRef.current = videoElement;
              videoContainerRef.current.innerHTML = '';
              videoContainerRef.current.appendChild(videoElement);

              videoElement.onloadedmetadata = () => {
                videoElement!.play().catch(console.error);
              };
            }
          }

          // Auto-start script after stream is ready
          if (!hasStartedRef.current) {
            hasStartedRef.current = true;
            setTimeout(() => {
              speakLine(0);
            }, 1000);
          }
        }, 200);
      });

      avatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
        if (!isLeavingRef.current) {
          setError('Connection lost. Please refresh to try again.');
        }
      });

      // Create avatar session - NO knowledgeBase to prevent automatic AI responses
      // We only want the avatar to speak when we explicitly call .speak()
      const voiceId = mode === 'voice' && companyConfig.avatar.voiceIdVoiceOnly
        ? companyConfig.avatar.voiceIdVoiceOnly
        : companyConfig.avatar.voiceId;

      await avatar.createStartAvatar({
        avatarName: companyConfig.avatar.avatarId,
        quality: AvatarQuality.Medium,
        voice: { voiceId },
        language: 'en-US',
        activityIdleTimeout: 600,
        useSilencePrompt: false, // Disable automatic silence prompts
        // NO knowledgeBase - avatar should be silent until we call .speak()
      });

      // NOTE: We intentionally do NOT use startVoiceChat() for the scripted demo.
      // startVoiceChat enables two-way AI conversation which would override our script.
      // Instead, users advance the script using spacebar only.
      // This keeps the demo fully scripted without any AI-generated responses.
      console.log('[Script] Scripted mode - spacebar only (no voice chat)');

    } catch (error) {
      console.error('[Script] Failed to initialize avatar:', error);
      setError('Failed to initialize assistant. Please refresh and try again.');
      setIsInitializing(false);
    }
  };

  const handleStartCall = async () => {
    try {
      setIsCallActive(true);
      await initializeUserMedia();
      await initializeAvatar();
    } catch (error) {
      console.error('[Script] Failed to start call:', error);
      setIsInitializing(false);
    }
  };

  const handleEndCall = async () => {
    isLeavingRef.current = true;
    setIsCallActive(false);

    if (avatarRef.current) {
      try {
        await avatarRef.current.stopAvatar();
      } catch (error) {
        console.error('[Script] Error stopping avatar:', error);
      }
      avatarRef.current = null;
    }

    if (userStream) {
      userStream.getTracks().forEach((track) => track.stop());
    }

    navigate(`/avatar?company=${companyConfig.id}`);
  };

  const toggleAudio = () => {
    if (userStream) {
      const audioTrack = userStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const toggleSpeaker = () => {
    if (avatarVideoElementRef.current) {
      avatarVideoElementRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  const handleQuizAnswer = (answer: string) => {
    setUserQuizAnswer(answer);
    setQuizState('answered');
  };

  // ==========================================================================
  // RENDER - Exact copy of UnifiedAvatarCall UI
  // ==========================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/avatar?company=${companyConfig.id}`)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Building2
                className="w-5 h-5"
                style={{ color: companyConfig.colors.primary }}
              />
              <h1 className="text-lg font-semibold">
                {companyConfig.name} - {companyConfig.avatar.coachName}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {isCallActive && !isInitializing && (
              <div className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                {stageNames[currentStage]}
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Powered by</span>
              <img
                src="/logos/Hupo_Logotype_Orange(noR).svg"
                alt="Hupo"
                className="h-5"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4">
        {/* Loading State */}
        {isInitializing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-[600px]"
          >
            <Loader2
              className="w-12 h-12 animate-spin mb-4"
              style={{ color: companyConfig.colors.primary }}
            />
            <h3 className="text-xl font-semibold mb-2">
              Connecting to {companyConfig.avatar.coachName}...
            </h3>
            <p className="text-slate-600">
              Setting up your {mode === 'video' ? 'video' : 'voice'} session
            </p>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto mt-20 bg-red-50 border border-red-200 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Connection Error
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </motion.div>
        )}

        {/* Active Call */}
        {isCallActive && !isInitializing && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid lg:grid-cols-3 gap-4"
          >
            {/* Main video area */}
            <div className="lg:col-span-2 space-y-4">
              <div className="relative bg-slate-900 rounded-xl overflow-hidden aspect-video shadow-2xl">
                {/* Video container */}
                <div ref={videoContainerRef} className="w-full h-full" />

                {/* UOB Visuals Overlay */}
                {companyConfig.id === 'uob' && (
                  <UOBVisuals
                    currentStage={currentStage}
                    quizQuestion={quizQuestion}
                    activePrinciple={activePrinciple}
                    exemptMode={exemptMode}
                    quizState={quizState}
                    userQuizAnswer={userQuizAnswer}
                    onQuizAnswer={handleQuizAnswer}
                  />
                )}

                {/* Avatar name overlay - hide when UOB visuals are showing */}
                {!(companyConfig.id === 'uob' && (
                  (currentStage === 1 && activePrinciple) ||
                  (currentStage === 2 && exemptMode) ||
                  currentStage === 3
                )) && (
                  <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                    <p className="text-white text-sm font-medium">
                      {companyConfig.avatar.coachName}
                    </p>
                    <p className="text-white/70 text-xs">
                      {companyConfig.id === 'boi' ? 'Financial Coach' : 'Training Coach'}
                    </p>
                  </div>
                )}

                {/* Processing indicator */}
                {isProcessing && (
                  <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-1.5 z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-white text-sm">Speaking...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Control bar - Same as UnifiedAvatarCall */}
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center justify-between px-4">
                  {/* Status Indicator */}
                  <div className="flex items-center gap-3 w-1/3">
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300",
                      isListening
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : isProcessing
                          ? "bg-blue-100 text-blue-700 border border-blue-200"
                          : "bg-slate-100 text-slate-500 border border-slate-200"
                    )}>
                      {isListening ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                          </span>
                          Your Turn - Speak
                        </>
                      ) : isProcessing ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Speaking
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 rounded-full bg-slate-400" />
                          Ready
                        </>
                      )}
                    </div>
                  </div>

                  {/* Center Controls */}
                  <div className="flex items-center justify-center gap-4 w-1/3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={toggleAudio}
                      className={cn(
                        "rounded-full w-12 h-12 transition-all duration-200",
                        !isAudioOn && "bg-red-50 text-red-500 border-red-200 hover:bg-red-100 hover:text-red-600"
                      )}
                    >
                      {isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                    </Button>

                    <Button
                      variant="destructive"
                      size="lg"
                      onClick={handleEndCall}
                      className="rounded-full px-8 h-12 gap-2 shadow-md hover:shadow-lg transition-all"
                    >
                      <PhoneOff className="w-5 h-5" />
                      <span className="hidden sm:inline">End Call</span>
                    </Button>
                  </div>

                  {/* Right - Speaker toggle */}
                  <div className="w-1/3 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={toggleSpeaker}
                      className="rounded-full text-slate-500 hover:text-slate-700"
                    >
                      {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Side panel - Progress tracker */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm border p-6 h-full">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-6">Session Progress</h3>
                <div className="space-y-0 relative">
                  {/* Vertical line connecting steps */}
                  <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-slate-100" />

                  {stageNames.map((stage, idx) => {
                    const isCompleted = idx < currentStage;
                    const isCurrent = idx === currentStage;
                    const isUpcoming = idx > currentStage;

                    return (
                      <div
                        key={idx}
                        className={cn(
                          'relative flex gap-4 pb-8 last:pb-0 group',
                          isUpcoming && 'opacity-50',
                        )}
                      >
                        {/* Step Indicator */}
                        <div className="relative z-10 flex-shrink-0">
                          <motion.div
                            initial={false}
                            animate={{
                              backgroundColor: isCompleted ? '#22c55e' : isCurrent ? companyConfig.colors.primary : '#f1f5f9',
                              borderColor: isCurrent ? companyConfig.colors.primary : 'transparent',
                              scale: isCurrent ? 1.1 : 1,
                            }}
                            className={cn(
                              'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors duration-300',
                              isCompleted ? 'text-white' : isCurrent ? 'text-white shadow-lg' : 'text-slate-400'
                            )}
                          >
                            {isCompleted ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span>{idx + 1}</span>
                            )}
                          </motion.div>

                          {/* Pulse effect for current step */}
                          {isCurrent && (
                            <div
                              className="absolute inset-0 rounded-full animate-ping opacity-20"
                              style={{ backgroundColor: companyConfig.colors.primary }}
                            />
                          )}
                        </div>

                        {/* Step Content */}
                        <div className={cn("pt-1 transition-all duration-300", isCurrent ? "translate-x-1" : "")}>
                          <p
                            className={cn(
                              "text-sm font-semibold leading-none mb-1 transition-colors",
                              isCurrent ? "text-slate-900" : "text-slate-600"
                            )}
                          >
                            {stage}
                          </p>
                          <p className="text-xs text-slate-500">
                            {isCompleted ? 'Completed' : isCurrent ? 'In Progress' : 'Upcoming'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
